import bs58 from 'bs58';
import { AbiCoder, keccak256, solidityPackedKeccak256, toUtf8Bytes } from 'ethers';

export interface SolanaMessageSigner {
  getAddress(): Promise<string>;
  signMessage(message: Uint8Array): Promise<string>;
}

const abiCoder = AbiCoder.defaultAbiCoder();

function encodeMessageHash(encoded: string): Uint8Array {
  const messageHash = keccak256(encoded);
  return new TextEncoder().encode(messageHash.slice(2));
}

function decodeSolanaAddress(address: string): string {
  return `0x${Buffer.from(bs58.decode(address)).toString('hex')}`;
}

export async function signSolanaRegistration(
  signer: SolanaMessageSigner,
  data: { brokerId: string; chainId: number; timestamp: number; registrationNonce: string },
): Promise<string> {
  const brokerIdHash = solidityPackedKeccak256(['string'], [data.brokerId]);
  const encoded = abiCoder.encode(
    ['bytes32', 'uint256', 'uint256', 'uint256'],
    [brokerIdHash, BigInt(data.chainId), BigInt(data.timestamp), BigInt(data.registrationNonce)],
  );
  return signer.signMessage(encodeMessageHash(encoded));
}

export async function signSolanaAddOrderlyKey(
  signer: SolanaMessageSigner,
  data: { brokerId: string; chainId: number; orderlyKey: string; scope: string; timestamp: number; expiration: number; chainType?: 'EVM' | 'SOL' },
): Promise<string> {
  const brokerIdHash = solidityPackedKeccak256(['string'], [data.brokerId]);
  const orderlyKeyHash = solidityPackedKeccak256(['string'], [data.orderlyKey]);
  const scopeHash = solidityPackedKeccak256(['string'], [data.scope]);
  const encoded = abiCoder.encode(
    ['bytes32', 'bytes32', 'bytes32', 'uint256', 'uint256', 'uint256'],
    [
      brokerIdHash,
      orderlyKeyHash,
      scopeHash,
      BigInt(data.chainId),
      BigInt(data.timestamp),
      BigInt(data.expiration),
    ],
  );
  return signer.signMessage(encodeMessageHash(encoded));
}

export async function signSolanaSettlePnl(
  signer: SolanaMessageSigner,
  data: { brokerId: string; chainId: number; settleNonce: number; timestamp: number },
): Promise<string> {
  const brokerIdHash = solidityPackedKeccak256(['string'], [data.brokerId]);
  const encoded = abiCoder.encode(
    ['bytes32', 'uint256', 'uint64', 'uint64'],
    [brokerIdHash, BigInt(data.chainId), BigInt(data.settleNonce), BigInt(data.timestamp)],
  );
  return signer.signMessage(encodeMessageHash(encoded));
}

export async function signSolanaWithdraw(
  signer: SolanaMessageSigner,
  data: { brokerId: string; chainId: number; receiver: string; token: string; amount: bigint; withdrawNonce: number; timestamp: number },
): Promise<string> {
  const brokerIdHash = solidityPackedKeccak256(['string'], [data.brokerId]);
  const tokenHash = solidityPackedKeccak256(['string'], [data.token]);
  const salt = keccak256(toUtf8Bytes('Orderly Network'));
  const encoded = abiCoder.encode(
    ['bytes32', 'bytes32', 'uint256', 'bytes32', 'uint256', 'uint64', 'uint64', 'bytes32'],
    [
      brokerIdHash,
      tokenHash,
      BigInt(data.chainId),
      decodeSolanaAddress(data.receiver),
      data.amount,
      BigInt(data.withdrawNonce),
      BigInt(data.timestamp),
      salt,
    ],
  );
  return signer.signMessage(encodeMessageHash(encoded));
}
